// src/components/enrollments/EnrollmentList.jsx
import ExcelDownloadButton from "../common/ExcelDownloadButton";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { listEnrollments, archiveEnrollment, activateEnrollment, checkArchiveEnrollment } from "../../api/enrollments";
import { useNavigate } from "react-router-dom";
import PaginationFooter from "../common/PaginationFooter";
import SortableTableHeader from "../common/SortableTableHeader";
import useSortedClientPagination from "../../hooks/useSortedClientPagination";
import { enrollmentListSortColumns } from "../../config/tableSortColumns";
import { formatDateTime, formatDate, todayIso } from "../../utils/date";
import { formatDiscountValue } from "../../utils/currency";
import { useCurrencyFormatter } from "../../context/RegionalSettingsContext";
import UserLink from "../common/UserLink";
import UserStatusBadge from "../common/UserStatusBadge";
import ArchiveEnrollmentModal from "./ArchiveEnrollmentModal";
import {
    formatChargesMode,
    isPeriodBillingMode,
    periodChargeStatusLabel,
} from "../../utils/periodBillingCopy";
import { localizeBatchName } from '../../utils/displayNames';
import ListPageLayout, {
    ListPageHeader,
    ListFilterTabs,
    ListSearchCard,
    ListLoadingSpinner,
    ListEmptyState,
    ListTableCard,
} from "../layout/ListPageLayout";
import { apiErrorMessage } from '../../utils/apiError';


const useDebounced = (value, delay = 250) => {
    const [v, setV] = useState(value);
    useEffect(() => { const t = setTimeout(() => setV(value), delay); return () => clearTimeout(t); }, [value, delay]);
    return v;
};

const EnrollmentList = ({ role = "ADMIN" }) => {
    const { t } = useTranslation();
    const formatMoney = useCurrencyFormatter();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [query, setQuery] = useState("");
    const debouncedQuery = useDebounced(query);
    const navigate = useNavigate();
    const [filter, setFilter] = useState("active");
    const [crossBranchModal, setCrossBranchModal] = useState(null);
    const [crossBranchArchiving, setCrossBranchArchiving] = useState(false);
    const [archiveModal, setArchiveModal] = useState(null);
    const [archiving, setArchiving] = useState(false);

    const rolePath = role?.toLowerCase?.() || "admin";

    const fetchData = async () => {
        setLoading(true);
        setLoadError(false);
        try {
            const r = await listEnrollments(rolePath, { active: undefined });
            const data = r?.data ?? r ?? [];
            setRows(
                data.map((row) => ({
                    ...row,
                    enrollmentId: row.enrollmentId ?? row.enrollment_id ?? row.id,
                    createdAt: row.created ?? row.createdAt ?? row.enrolledAt ?? row.created_at,
                }))
            );
            setLoadError(false);
        } catch {
            setRows([]);
            setLoadError(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

    const openArchiveModal = (row) => {
        setArchiveModal({
            enrollmentId: row.enrollmentId,
            studentName: row.studentName,
            enrolledDate: row.enrolledDate,
            defaultEndIso: row.endEnrollmentDate ?? todayIso(),
        });
    };

    const handleArchive = async (row) => {
        if (role === "MANAGER") {
            try {
                const res = await checkArchiveEnrollment(row.enrollmentId);
                const info = res?.data ?? res;
                if (info?.hasCrossBranchEnrollments) {
                    setCrossBranchModal({
                        enrollmentId: row.enrollmentId,
                        studentName: info.studentName,
                        crossBranchEnrollments: info.crossBranchEnrollments,
                        enrolledDate: row.enrolledDate,
                        defaultEndIso: row.endEnrollmentDate,
                    });
                    return;
                }
            } catch (err) {
                alert(apiErrorMessage(err, t("enrollments.cannotArchive")));
                return;
            }
        }
        openArchiveModal(row);
    };

    const confirmArchive = async (endEnrollmentDate) => {
        if (!archiveModal) return;
        setArchiving(true);
        try {
            await archiveEnrollment(rolePath, archiveModal.enrollmentId, endEnrollmentDate);
            setArchiveModal(null);
            fetchData();
        } catch (err) {
            alert(apiErrorMessage(err, t("enrollments.archiveFailed")));
        } finally {
            setArchiving(false);
        }
    };

    const renewPath = (row) => {
        const studentId = row.studentId ?? row.student_id;
        const levelId = row.levelId ?? row.level_id;
        const batchId = row.batchId ?? row.batch_id;
        if (studentId == null || levelId == null || batchId == null || row.enrollmentId == null) {
            return null;
        }
        const params = new URLSearchParams({
            renewFrom: String(row.enrollmentId),
            studentId: String(studentId),
            levelId: String(levelId),
            batchId: String(batchId),
        });
        return `/${rolePath}/enrollments/new?${params.toString()}`;
    };

    const filtered = useMemo(() => {
        let result = rows;
        if (filter === "active") {
            result = result.filter(r => r.active === true);
        } else if (filter === "archived") {
            result = result.filter(r => r.active === false);
        }
        const q = debouncedQuery.trim().toLowerCase();
        if (q) {
            result = result.filter((r) => {
                const hay = [
                    r.enrollmentId, r.studentId ?? r.student_id, r.studentName,
                    r.batchName, r.levelName, r.periodChargeStatus, r.createdAt ?? r.created
                ].filter(Boolean).join(" ").toLowerCase();
                return hay.includes(q);
            });
        }
        return result;
    }, [rows, debouncedQuery, filter]);

    const pagination = useSortedClientPagination(filtered, {
        columns: enrollmentListSortColumns,
        resetKey: `${filter}-${debouncedQuery}`,
    });

    const Row = ({ r }) => {
        const chargesMode = r.overrideChargesMode ?? r.chargesMode ?? "PER_SESSION_HOURLY";
        const chargesModeDisplay = formatChargesMode(chargesMode);
        const periodBilling = isPeriodBillingMode(chargesMode);
        const paymentBadge = periodChargeStatusLabel(r.periodChargeStatus);

        const hasDiscount = r.appliedDiscountValue && r.appliedDiscountType;
        const discountDisplay = hasDiscount
            ? `${formatDiscountValue(r.appliedDiscountType, r.appliedDiscountValue)}${r.appliedDiscountName ? ` - ${r.appliedDiscountName}` : ""}`
            : "-";

        const startDate = r.enrolledDate;
        const endDate = r.endEnrollmentDate ?? r.end_enrollment_date;
        const periodDisplay = startDate && endDate
            ? `${formatDate(startDate)} – ${formatDate(endDate)}`
            : startDate
                ? `${formatDate(startDate)} – ${t("enrollments.periodOngoing")}`
                : "-";

        const endDateDisplay = endDate ? formatDate(endDate) : "-";
        const isExpired = endDate && endDate < todayIso();
        const daysLeft = r.daysUntilExpiry;
        const showRenew = periodBilling && (!r.active || isExpired);

        return (
            <tr className={`table-row ${isExpired && r.active ? "bg-yellow-50" : ""}`}>
                <td className="table-cell whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-1 rounded-lg bg-gradient-to-r from-purple-100 to-indigo-100 text-purple-800 text-sm font-medium">
                        #{r.enrollmentId}
                    </span>
                </td>
                <td className="table-cell">
                    <div>
                        <UserLink id={r.studentId} name={r.studentName || "-"} />
                        <div className="text-xs text-gray-500 mt-1">{t("common.id")} {r.studentId ?? r.student_id}</div>
                    </div>
                </td>
                <td className="table-cell">
                    {r.levelName
                        ? <div><div className="font-medium">{r.levelName}</div><div className="text-xs text-gray-500">#{r.levelId ?? r.level_id ?? ""}</div></div>
                        : (r.levelId ?? r.level_id ? `#${r.levelId ?? r.level_id}` : <span className="text-gray-400">-</span>)}
                </td>
                <td className="table-cell">
                    {r.batchName
                        ? <div><div className="font-medium">{localizeBatchName(r.batchName, t)}</div><div className="text-xs text-gray-500">#{r.batchId ?? r.batch_id ?? ""}</div></div>
                        : (r.batchId ?? r.batch_id ? `#${r.batchId ?? r.batch_id}` : <span className="text-gray-400">-</span>)}
                </td>
                <td className="table-cell whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                        {chargesModeDisplay}
                    </span>
                </td>
                <td className="table-cell text-sm whitespace-nowrap">{periodDisplay}</td>
                <td className="table-cell whitespace-nowrap">
                    {periodBilling ? (
                        paymentBadge ? (
                            <div className="space-y-1">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${paymentBadge.className}`}>
                                    {paymentBadge.text}
                                </span>
                                {r.periodChargeAmount != null && (
                                    <div className="text-xs text-gray-600">{formatMoney(r.periodChargeAmount)}</div>
                                )}
                            </div>
                        ) : (
                            <span className="text-gray-400 text-sm">—</span>
                        )
                    ) : (
                        <span className="text-gray-400 text-sm">{t("enrollments.notApplicable")}</span>
                    )}
                </td>
                <td className="table-cell whitespace-nowrap">
                    {r.active && daysLeft != null && daysLeft >= 0 && daysLeft <= 30 ? (
                        <span className={`text-xs font-medium ${daysLeft <= 7 ? "text-red-600" : "text-amber-700"}`}>
                            {daysLeft === 0 ? t("enrollments.expiresToday") : t("enrollments.daysLeft", { count: daysLeft })}
                        </span>
                    ) : isExpired ? (
                        <span className="text-xs text-red-600 font-medium">{t("enrollments.expired")}</span>
                    ) : (
                        <span className="text-sm text-gray-700">{endDateDisplay}</span>
                    )}
                </td>
                <td className="table-cell">
                    <UserStatusBadge active={r.active} />
                </td>
                <td className="table-cell whitespace-nowrap">
                    <div className="flex flex-wrap items-center gap-2">
                        {showRenew && (
                            <button
                                type="button"
                                onClick={() => {
                                    const path = renewPath(r);
                                    if (!path) {
                                        alert(t('enrollments.renewMissingData'));
                                        return;
                                    }
                                    navigate(path);
                                }}
                                className="btn-action-edit"
                                title={t("enrollments.renewTitle")}
                            >
                                {t("enrollments.renew")}
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={() => navigate(`/${rolePath}/enrollments/${r.enrollmentId}`)}
                            className="btn-action-view"
                            title={t("enrollments.viewTitle")}
                        >
                            {t("enrollments.view")}
                        </button>
                        {role === "ADMIN" && (
                            <button
                                type="button"
                                onClick={() => navigate(`/${rolePath}/enrollments/${r.enrollmentId}/edit`)}
                                className="btn-action-edit"
                                title={t("enrollments.editTitle")}
                            >
                                {t("enrollments.edit")}
                            </button>
                        )}
                        {r.active ? (
                            <button
                                type="button"
                                onClick={() => handleArchive(r)}
                                className="btn-action-archive"
                            >
                                {t("enrollments.archive")}
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={async () => {
                                    if (!window.confirm(t("enrollments.reactivateConfirm", { id: r.enrollmentId }))) return;
                                    try {
                                        await activateEnrollment(rolePath, r.enrollmentId);
                                        fetchData();
                                    } catch (err) {
                                        alert(apiErrorMessage(err, t('enrollments.activateFailed')));
                                    }
                                }}
                                className="btn-action-activate"
                            >
                                {t("enrollments.activate")}
                            </button>
                        )}
                    </div>
                </td>
            </tr>
        );
    };

    const filterTabs = [
        { key: "all", label: t("enrollments.filters.all"), count: rows.length },
        { key: "active", label: t("enrollments.filters.active"), count: rows.filter(r => r.active).length },
        { key: "archived", label: t("enrollments.filters.archived"), count: rows.filter(r => !r.active).length },
    ];

    return (
        <ListPageLayout>
            <ListPageHeader
                title={t("enrollments.title")}
                subtitle={t("enrollments.subtitle")}
                actions={(
                    <>
                        <ExcelDownloadButton
                            columns={[
                                { header: t("enrollments.columns.id"), key: 'enrollmentId', width: 8 },
                                { header: t("enrollments.columns.student"), key: 'studentName', width: 24 },
                                { header: t("enrollments.columns.level"), key: 'levelName', width: 20 },
                                { header: t("enrollments.columns.batch"), key: 'batchName', render: (v) => localizeBatchName(v, t), width: 22 },
                                { header: t("enrollments.columns.billing"), key: 'chargesMode', render: (v, row) => formatChargesMode(row.overrideChargesMode ?? row.chargesMode), width: 20 },
                                { header: t("enrollments.columns.period"), key: 'enrolledDate', render: (v, row) => {
                                    const end = row.endEnrollmentDate ?? row.end_enrollment_date;
                                    return end ? `${v} – ${end}` : v;
                                }, width: 22 },
                                { header: t("enrollments.columns.payment"), key: 'periodChargeStatus', render: (v) => periodChargeStatusLabel(v)?.text || v || '', width: 12 },
                                { header: t("enrollments.columns.status"), key: 'active', render: (v) => v ? t("enrollments.statusActive") : t("enrollments.statusArchived"), width: 12 },
                            ]}
                            data={filtered}
                            filename="enrollments"
                            disabled={filtered.length === 0}
                        />
                        <button
                            onClick={() => navigate(`/${rolePath}/enrollments/new`)}
                            className="btn-page-action-primary"
                        >
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            {t("enrollments.newEnrollment")}
                        </button>
                    </>
                )}
            />

            <ListFilterTabs tabs={filterTabs} active={filter} onChange={setFilter} />

            <ListSearchCard
                value={query}
                onChange={setQuery}
                placeholder={t("enrollments.searchPlaceholder")}
                shown={pagination.pageItems.length}
                total={filtered.length}
                entityLabel={t("enrollments.entityLabel")}
            />

            {loadError && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
                    {t('common.listLoadFailed')}
                </div>
            )}
            {loading ? (
                <ListLoadingSpinner />
            ) : loadError ? null : pagination.pageItems.length === 0 ? (
                <ListEmptyState
                    message={query ? t("enrollments.noMatch") : t("enrollments.empty")}
                    hint={query ? t("enrollments.emptyHint") : undefined}
                />
            ) : (
                <>
                <ListTableCard>
                    <div className="overflow-x-auto">
                        <table className="min-w-full table-auto">
                            <thead className="table-header">
                                <tr>
                                    <SortableTableHeader label={t("enrollments.columns.id")} columnKey="id" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t("enrollments.columns.student")} columnKey="student" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t("enrollments.columns.level")} columnKey="level" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t("enrollments.columns.batch")} columnKey="batch" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t("enrollments.columns.billing")} columnKey="chargesMode" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t("enrollments.columns.period")} columnKey="enrolledDate" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t("enrollments.columns.payment")} columnKey="periodChargeStatus" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t("enrollments.columns.expiry")} columnKey="endDate" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t("enrollments.columns.status")} columnKey="status" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <th className="table-header-cell">{t("enrollments.columns.actions")}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {pagination.pageItems.map((r) => <Row key={r.enrollmentId} r={r} />)}
                            </tbody>
                        </table>
                    </div>
                </ListTableCard>

                <PaginationFooter
                    page={pagination.page}
                    size={pagination.size}
                    total={pagination.total}
                    onPageChange={pagination.setPage}
                    onSizeChange={pagination.setSize}
                />
                </>
            )}

            <ArchiveEnrollmentModal
                open={!!archiveModal}
                enrollmentId={archiveModal?.enrollmentId}
                studentName={archiveModal?.studentName}
                enrolledDate={archiveModal?.enrolledDate}
                defaultEndIso={archiveModal?.defaultEndIso}
                archiving={archiving}
                onConfirm={confirmArchive}
                onCancel={() => setArchiveModal(null)}
            />

            {crossBranchModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full mx-4">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">{t("enrollments.crossBranchWarning")}</h3>
                                <p className="text-sm text-gray-500">{t("enrollments.crossBranchEnrollment", { id: crossBranchModal.enrollmentId })}</p>
                            </div>
                        </div>
                        <p className="text-sm text-gray-700 mb-3">
                            <Trans
                                i18nKey="enrollments.crossBranchDesc"
                                values={{ name: crossBranchModal.studentName }}
                                components={{ strong: <strong /> }}
                            />
                        </p>
                        <ul className="mb-4 space-y-1.5 max-h-40 overflow-y-auto">
                            {crossBranchModal.crossBranchEnrollments.map((e, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
                                    <span><strong>{e.branchName}</strong> — {e.levelName} / {localizeBatchName(e.batchName, t)}</span>
                                </li>
                            ))}
                        </ul>
                        <div className="flex gap-3 justify-end">
                            <button onClick={() => setCrossBranchModal(null)} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm">
                                {t("common.cancel")}
                            </button>
                            <button
                                disabled={crossBranchArchiving}
                                onClick={() => {
                                    setCrossBranchModal(null);
                                    openArchiveModal({
                                        enrollmentId: crossBranchModal.enrollmentId,
                                        studentName: crossBranchModal.studentName,
                                        enrolledDate: crossBranchModal.enrolledDate,
                                        endEnrollmentDate: crossBranchModal.defaultEndIso,
                                    });
                                }}
                                className="btn-action-archive disabled:opacity-60"
                            >
                                {t("enrollments.continueArchive")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </ListPageLayout>
    );
};

export default EnrollmentList;
